import { Button, DeprecatedDoneIcon as Done, Flex, Link, Modal } from '@lokalise/louis';
import { useTheme } from '@lokalise/styled';
import { useState } from 'react';

import { useUpdateTeamPreferenceMutation } from '@app/api/preferences';
import { showUnexpectedErrorToast } from '@app/components/Toast';
import { showNewMessage } from '@app/utils/intercom';
import { navigateToPath } from '@app/utils/location';

import withingsLogo from '../assets/static/withings-logo.png';
import withingsMobile from '../assets/static/withings-mobile.png';
import {
    BenefitItem,
    BenefitsGrid,
    KeepPlanBenefitsHeadline,
    KeepPlanHeadline,
    Quote,
    QuoteAuthor,
    QuoteBlock,
    QuoteText,
} from './styles';

const TrialOver = ({ teamId }: { teamId: string }) => {
    const [isOpen, setIsOpen] = useState(true);
    const [runningAction, setRunningAction] = useState<'upgrade' | 'dont-upgrade' | 'contact-sales' | undefined>();

    const isUpgrading = runningAction === 'upgrade';
    const isNotUpgrading = runningAction === 'dont-upgrade';
    const isContactingSales = runningAction === 'contact-sales';
    const isUpdateInProgress = isUpgrading || isNotUpgrading || isContactingSales;

    const closeDialog = () => setIsOpen(false);

    const [updateTeamPreference] = useUpdateTeamPreferenceMutation();

    const markTrialAsFinished = () =>
        updateTeamPreference({
            name: 'design_plugins_trial',
            teamId,
            value: 'trial_finished',
        });

    const upgrade = async () => {
        try {
            setRunningAction('upgrade');
            await markTrialAsFinished().unwrap();
            navigateToPath('/team/subscription');
        } catch {
            showUnexpectedErrorToast();
            setRunningAction(undefined);
        }
    };

    const dontUpgrade = async () => {
        try {
            setRunningAction('dont-upgrade');
            await markTrialAsFinished().unwrap();
            closeDialog();
        } catch {
            showUnexpectedErrorToast();
            setRunningAction(undefined);
        }
    };

    const contactSales = async () => {
        try {
            setRunningAction('contact-sales');
            await markTrialAsFinished().unwrap();
            closeDialog();
            showNewMessage();
        } catch {
            showUnexpectedErrorToast();
            setRunningAction(undefined);
        }
    };

    const theme = useTheme();

    return (
        <Modal title="Free trial of design plugins is over" onClose={dontUpgrade} opened={isOpen}>
            <KeepPlanHeadline>Would you like to upgrade to Pro Plan to retain the design plugins?</KeepPlanHeadline>
            <KeepPlanBenefitsHeadline>You also get</KeepPlanBenefitsHeadline>
            <BenefitsGrid>
                <BenefitItem>
                    <Done color={theme.color.custom.green[500]} size="21px" />
                    <strong>30000</strong> keys
                </BenefitItem>
                <BenefitItem>
                    <Done color={theme.color.custom.green[500]} size="21px" />
                    Apps and integrations
                </BenefitItem>
                <BenefitItem>
                    <Done color={theme.color.custom.green[500]} size="21px" />
                    Screenshots workflow
                </BenefitItem>
                <BenefitItem>
                    <Done color={theme.color.custom.green[500]} size="21px" />
                    <strong>15</strong> seats
                </BenefitItem>
                <BenefitItem>
                    <Done color={theme.color.custom.green[500]} size="21px" />
                    Branching
                </BenefitItem>
                <BenefitItem>
                    <Done color={theme.color.custom.green[500]} size="21px" />
                    Custom statuses
                </BenefitItem>
            </BenefitsGrid>

            <Flex gap={3}>
                <Button variant="primary" onClick={upgrade} loading={isUpgrading} disabled={isUpdateInProgress}>
                    Upgrade to Pro plan
                </Button>
                <Button
                    variant="secondary"
                    onClick={dontUpgrade}
                    loading={isNotUpgrading}
                    disabled={isUpdateInProgress}
                >
                    Don’t upgrade
                </Button>
                <Button onClick={contactSales} loading={isContactingSales} disabled={isUpdateInProgress}>
                    Contact sales
                </Button>
            </Flex>

            <QuoteBlock>
                <Quote>
                    <img src={withingsLogo} alt="withingsLogo" height="16" width="90" />
                    <QuoteText>
                        “By migrating to Lokalise, we’ve accelerated the delivery of new localized features by 90%.”
                    </QuoteText>
                    <QuoteAuthor>
                        Romain Dahan
                        <br />
                        Product Manager
                    </QuoteAuthor>
                    <Link href="https://lokalise.com/case-studies/withings" target="_blank" rel="noreferrer">
                        Read full story from Withings
                    </Link>
                </Quote>
                <img src={withingsMobile} alt="Withings mobile app" height="148" width="148" />
            </QuoteBlock>
        </Modal>
    );
};

export default TrialOver;
